<template>
  <div>
    <PurchaseFlowTabs
      :tabs="jointInfoTabs"
      :active-index="currentSubstepIndex"
      @tab-click="handleTabClick"
      :is-review="is_review"
      :is-modal-edit="is_modal_edit"
    />
    <JointContactSelection v-if="isSelectionSubstep" class="mt-5 md:mt-6" />
    <div
      v-if="jointInfoTabs[currentSubstepIndex].name === 'Residential Address' && !is_review"
      @click="handleJointResidentialSameAsPrimaryChange"
      class="bg-container-bg py-5 flex items-center px-3 mb-[0.875rem] border-2 rounded-xl cursor-pointer mt-5 md:mt-6"
      :class="
        purchaseFlowStore.joint_residential_same_as_primary
          ? 'border-radio-checkbox-outline'
          : 'border-container-bg'
      "
    >
      <div class="mr-5">
        <Icon
          v-if="purchaseFlowStore.joint_residential_same_as_primary"
          name="CircleCheck"
          class="w-6 h-6 fill-radio-checkbox-checked dark:fill-white stroke-container-bg"
        />
        <Icon v-else name="Circle" class="w-6 h-6 stroke-radio-checkbox-outline" />
      </div>

      <div class="font-inter text-lg font-medium md:font-semibold text-strongest">
        Same as primary investor's address
      </div>
    </div>
    <div
      v-if="
        jointInfoTabs[currentSubstepIndex].name === 'ID Information' && daysUntilIDExpires <= 30
      "
      class="p-4 bg-bg-canvas rounded-xl mb-[1.625rem] border border-outline-error mt-5 md:mt-6 animate-slideup"
    >
      <AsteriskNotice variant="error" size="md" class="mb-1" text="ID Info Expiring" />
      <div class="font-inter text-strongest font-medium" v-if="daysUntilIDExpires > 0">
        Your ID is expiring in {{ daysUntilIDExpires }} days. Please provide updated information.
      </div>
      <div class="font-inter text-strongest font-medium" v-else>
        Your ID has expired. Please provide updated information.
      </div>
    </div>
    <div
      :class="
        (jointInfoTabs[currentSubstepIndex].name === 'Residential Address' && !is_review) ||
        (jointInfoTabs[currentSubstepIndex].name === 'ID Information' &&
          daysUntilIDExpires <= 30)
          ? 'h-0'
          : 'h-4'
      "
    ></div>
    <Form
      :schema_array="schemaToUse"
      :state_object="stateObject"
      :record_id="purchaseFlowStore.active_joint_contact?.record_id"
      :variant="is_review ? 'primary' : 'secondary'"
      :is_disabled="
        is_review ||
        isSelectionSubstep ||
        (jointInfoTabs[currentSubstepIndex].name === 'Residential Address' &&
          purchaseFlowStore.joint_residential_same_as_primary)
      "
      class="mt-5 md:mt-6"
    />
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useSchemaStore, usePurchaseFlowStore, useDataStore } from "@/stores";
import { Form, AsteriskNotice, Icon } from "@/components";
import PurchaseFlowTabs from "./PurchaseFlowUtilities/PurchaseFlowTabs.vue";
import JointContactSelection from "./PurchaseFlowUtilities/JointContactSelection.vue";

const schemaStore = useSchemaStore();
const purchaseFlowStore = usePurchaseFlowStore();
const dataStore = useDataStore();

const emit = defineEmits(["substep-change"]);
const props = defineProps({
  is_review: { type: Boolean, default: false },
  is_modal_edit: { type: Boolean, default: false },
});

const localSubstep = ref(1);

const jointInfoTabs = computed(() => {
  const step = purchaseFlowStore.investment_steps.find((s) => s.name === "Joint Information");
  if (!step?.sub_steps) return [];

  return step.sub_steps
    .map((name) => {
      if (name === "Joint Contact Selection") return { name, schema_group: null };
      if (name === "Joint Information") return { name, schema_group: "Personal Information" };
      if (name === "Residential Address") return { name, schema_group: "Residential Address" };
      if (name === "ID Information") return { name, schema_group: "ID Information" };
      return { name, schema_group: null };
    })
    .filter(
      (tab) => !((props.is_review || props.is_modal_edit) && tab.name === "Joint Contact Selection")
    );
});

const currentSubstepIndex = computed(() => {
  if (props.is_review || props.is_modal_edit) return localSubstep.value - 1;
  return purchaseFlowStore.current_substep - 1;
});
const isSelectionSubstep = computed(() => {
  return jointInfoTabs.value[currentSubstepIndex.value]?.name === "Joint Contact Selection";
});
const hasSignedInvestmentForThisJoint = computed(() => {
  const jointId = purchaseFlowStore.active_joint_contact?.record_id;
  if (!jointId) return false;
  return dataStore.investments?.some(
    (inv) => inv.joint_contact_id === jointId && inv.signature_date && inv.status !== "Canceled"
  );
});

const schemaToUse = computed(() => {
  const fields = getSchemaForSubstep(currentSubstepIndex.value);
  if (!hasSignedInvestmentForThisJoint.value && fields) {
    return fields.map((f) => ({ ...f, read_only: false }));
  }
  return fields;
});
const stateObject = computed(() => getStateObjectForSubstep(currentSubstepIndex.value));
// In <script setup>
const daysUntilIDExpires = computed(() => {
  const id_expiration_date = purchaseFlowStore.active_joint_contact?.id_info?.id_expiration_date;
  if (!id_expiration_date) return 1000;
  const today = new Date();
  const expirationDate = new Date(id_expiration_date);
  const timeDiff = expirationDate.getTime() - today.getTime();
  return Math.ceil(timeDiff / (1000 * 3600 * 24));
});

function getSchemaForSubstep(index) {
  const tab = jointInfoTabs.value[index];
  return purchaseFlowStore.accredited_joint_contact_personal_info_schema.find(
    (schema) => schema.group_name === tab?.schema_group
  )?.fields;
}

function getStateObjectForSubstep(index) {
  const tab = jointInfoTabs.value[index];
  return tab?.name === "ID Information"
    ? purchaseFlowStore.active_joint_contact.id_info
    : purchaseFlowStore.active_joint_contact;
}

function handleTabClick(index) {
  const targetSubstep = index + 1;
  const currentSubstep = localSubstep.value;

  if (props.is_review) {
    localSubstep.value = targetSubstep;
    return;
  }

  if (props.is_modal_edit) {
    if (targetSubstep <= currentSubstep) {
      localSubstep.value = targetSubstep;
      return;
    }
    // Forward — validate in-between substeps
    const result = validateRange(currentSubstep - 1, index);
    if (!result.proceed) {
      localSubstep.value = result.failedAt + 1;
      return;
    }

    localSubstep.value = targetSubstep;
    return;
  }

  // Normal flow — let parent handle it
  emit("substep-change", targetSubstep);
}

function validateSubstep(index) {
  const schema = getSchemaForSubstep(index);
  if (!schema) return { proceed: true }; // No schema means no validation needed - (select existing joint contact substep)

  const result = schemaStore.validateFields(
    purchaseFlowStore.active_joint_contact,
    schema,
    purchaseFlowStore.active_joint_contact.record_id,
    true
  );

  return {
    proceed: result.proceed,
    invalid_fields: result.invalid_fields,
    tabName: jointInfoTabs.value[index]?.name,
    error_messages: result.error_messages,
    field_errors: result.field_errors,
  };
}

function validateRange(fromIndex, toIndex) {
  for (let i = fromIndex; i < toIndex; i++) {
    const result = validateSubstep(i);
    if (!result.proceed) {
      return {
        proceed: false,
        failedAt: i,
        tabName: result.tabName,
        invalid_fields: result.invalid_fields,
        error_messages: result.error_messages,
        field_errors: result.field_errors,
      };
    }
  }
  return { proceed: true };
}

defineExpose({
  validate() {
    const result = validateSubstep(currentSubstepIndex.value);
    // if (!result.proceed) console.log("Validation failed:", result.invalid_fields);
    return result;
  },
  validateRange,
  afterContinue: async () => {
    await Promise.all([
      purchaseFlowStore.saveJointProgress(),
      purchaseFlowStore.saveInvestmentProgress(),
    ]);
  },
  substepCount: jointInfoTabs.value.length,
});

function handleJointResidentialSameAsPrimaryChange() {
  purchaseFlowStore.joint_residential_same_as_primary =
    !purchaseFlowStore.joint_residential_same_as_primary;
  if (purchaseFlowStore.joint_residential_same_as_primary) {
    syncJointResidentialToPrimary();
  }
}

function syncJointResidentialToPrimary() {
  const joint = purchaseFlowStore.active_joint_contact;
  if (!joint) return;
  const primary = dataStore.contact;
  joint.street1 = primary.street1;
  joint.street2 = primary.street2;
  joint.city = primary.city;
  joint.state = primary.state;
  joint.zip_code = primary.zip_code;
  joint.country = primary.country;
}

watch(
  () => [
    dataStore.contact.street1,
    dataStore.contact.street2,
    dataStore.contact.city,
    dataStore.contact.state,
    dataStore.contact.zip_code,
    dataStore.contact.country,
  ],
  () => {
    if (purchaseFlowStore.joint_residential_same_as_primary) {
      syncJointResidentialToPrimary();
    }
  },
  { immediate: true }
);

// Reset the flag when switching to a different joint contact so it doesn't
// carry over (and lock the form) onto a contact it was never set for.
// A change from no record_id to a record_id just means the active contact
// was saved for the first time, not a switch — skip the reset in that case.
watch(
  () => purchaseFlowStore.active_joint_contact?.record_id,
  (newId, oldId) => {
    if (oldId === undefined || oldId === newId) return;
    purchaseFlowStore.joint_residential_same_as_primary = false;
  }
);
</script>
